// ============================================================
// Loading a body plate's art (plan body-map, Track A; vehicles P6).
//
// Each traced figure or vehicle is a JSON asset (src/assets/plates/), fetched
// only when a plate first needs it: Vite turns the glob below into one lazy
// chunk per asset, none of them in the entry bundle. An asset is read and
// checked once (readPlateArt / readVehicleArt) and the promise cached, so every
// plate of it shares one parse.
// ============================================================

import { readPlateArt, PLATE_FIGURE_IDS, type PlateArt, type PlateFigureId } from '../../engine/plate/figureArt';
import { readVehicleArt, type VehicleArt } from '../../engine/plate/vehicleArt';
import { VEHICLE_KINDS, type VehicleKind } from '../../engine/vehicleKind';

const LOADERS = import.meta.glob<{ default: unknown }>('../../assets/plates/*.json');
const cache = new Map<string, Promise<unknown>>();

/**
 * One asset by name, read once. Rejects (never resolves to partial art) when there is no such asset or it does not
 * read; a rejected load is not cached, so a later call tries again.
 */
function loadAsset<T>(name: string, known: boolean, read: (raw: unknown) => T): Promise<T> {
  const hit = cache.get(name);
  if (hit) return hit as Promise<T>;
  const load = LOADERS[`../../assets/plates/${name}.json`];
  if (!known || !load) return Promise.reject(new Error(`no plate art for ${name}`));
  const art = load().then((m) => read(m.default));
  cache.set(name, art);
  art.catch(() => cache.delete(name));
  return art;
}

/** A person figure's art, loaded once. */
export const loadPlateArt = (id: PlateFigureId): Promise<PlateArt> =>
  loadAsset(id, PLATE_FIGURE_IDS.includes(id), readPlateArt);

/** A vehicle's art, loaded once. */
export const loadVehicleArt = (kind: VehicleKind): Promise<VehicleArt> =>
  loadAsset(`vehicle-${kind}`, VEHICLE_KINDS.includes(kind), readVehicleArt);
